// frontend/client/src/services/apiClient.js - ENHANCED WITH PUBLISHING SERVICE

import axios from 'axios';
import { getAuth } from 'firebase/auth';

// --- Centralized Axios Instance ---
const apiClient = axios.create({
  baseURL: process.env.REACT_APP_API_URL || '/api',
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 55000, // 55s — allows video publishing polling to complete within Vercel's 60s maxDuration // Increased to 30s for publishing operations
});

// --- Auth Interceptor ---
apiClient.interceptors.request.use(
  async (config) => {
    const auth = getAuth();
    const user = auth.currentUser;

    if (user) {
      try {
        const token = await user.getIdToken();
        config.headers.Authorization = `Bearer ${token}`;
        config.headers['X-Firebase-UID'] = user.uid;
      } catch (error) {
        console.error("Could not get Firebase token", error);
      }
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// --- Response & Error Handling ---
const handleResponse = (response) => {
  return { success: true, ...response.data };
};

const handleError = (error) => {
  let errorResponse;
  
  const GENERIC_ERROR_MESSAGE = 'We encountered an issue. Please try again later.';

  if (error.response) {
    console.error('API Error Response:', error.response.data);
    
    const receivedError = error.response.data.error || error.response.data.message;
    const errorDetails = error.response.data.details;

    const isTechnicalError = 
      error.response.status >= 500 || 
      (receivedError && 
       (receivedError.includes('MongoDB') || 
        receivedError.includes('Database') || 
        receivedError.includes('Failed to save profile') ||
        receivedError.includes('DB_PERSISTENCE_FAILED') ||
        receivedError.includes('API endpoint not found')));

    const finalError = isTechnicalError ? GENERIC_ERROR_MESSAGE : receivedError;

    errorResponse = { 
      success: false, 
      status: error.response.status,
      error: finalError || GENERIC_ERROR_MESSAGE,
      details: errorDetails, // Preserve details for debugging
      code: error.response.data.code
    };

  } else if (error.request) {
    console.error('API Network Error:', error.request);
    errorResponse = { success: false, error: 'Network Error: The server did not respond.' };
  } else {
    console.error('API Request Setup Error:', error.message);
    errorResponse = { success: false, error: 'Request setup failed.' };
  }
  
  if (!errorResponse.error || errorResponse.error === 'Internal Server Error') {
      errorResponse.error = GENERIC_ERROR_MESSAGE;
  }
  
  return errorResponse;
};

// --- Base API Methods ---
export const api = {
  get: (url, params) => {
    return apiClient.get(url, { params }).then(handleResponse).catch(handleError);
  },
  post: (url, data) => apiClient.post(url, data).then(handleResponse).catch(handleError),
  put: (url, data) => apiClient.put(url, data).then(handleResponse).catch(handleError),
  delete: (url, data) => apiClient.delete(url, { data }).then(handleResponse).catch(handleError),
};

// --- Publishing Service ---
export const publishingService = {
  /**
   * Publish content to one or more platforms
   * @param {string} firebaseUID - User's Firebase UID
   * @param {string[]} platforms - Array of platform names (e.g., ['tiktok', 'instagram'])
   * @param {Array} content - Array of content objects with caption, videoUrl, etc.
   * @param {boolean} enableCrossPromotion - Whether to enable cross-promotion
   * @returns {Promise<Object>} Publishing results
   */
  async publishContent(firebaseUID, platforms, content, enableCrossPromotion = false) {
    try {
      console.log('[PublishingService] Publishing content:', {
        firebaseUID: firebaseUID?.substring(0, 10) + '...',
        platforms,
        contentCount: content.length,
        enableCrossPromotion
      });

      // Validation
      if (!firebaseUID) {
        throw new Error('Firebase UID is required for publishing');
      }

      if (!platforms || platforms.length === 0) {
        throw new Error('At least one platform must be selected');
      }

      if (!content || content.length === 0) {
        throw new Error('Content is required for publishing');
      }

      // Log content structure for debugging
      content.forEach((item, index) => {
        console.log(`[PublishingService] Content[${index}]:`, {
          hasCaption: !!item.caption,
          hasVideoUrl: !!item.videoUrl,
          hasImageUrl: !!item.imageUrl,
          hasMediaUrl: !!item.mediaUrl,
          videoUrlPreview: item.videoUrl?.substring(0, 50) + '...'
        });
      });

      const response = await api.post('/publishing', {
        firebaseUID,
        platforms,
        content,
        enableCrossPromotion
      });

      console.log('[PublishingService] Response:', response);

      // Check if all platforms failed
      if (!response.success && response.details) {
        const allFailed = response.details.every(d => d.status === 'failed');
        if (allFailed) {
          // Extract the most specific error message
          const firstError = response.details[0];
          const errorMsg = firstError.error || response.error || 'Publishing failed';
          
          console.error('[PublishingService] All platforms failed:', response.details);
          
          return {
            success: false,
            error: errorMsg,
            details: response.details,
            allFailed: true
          };
        }
      }

      return response;
    } catch (error) {
      console.error('[PublishingService] Error:', error);
      
      // If it's already a formatted error response, return it
      if (error.success === false) {
        return error;
      }
      
      // Otherwise format it
      return {
        success: false,
        error: error.message || 'Failed to publish content',
        details: error.details
      };
    }
  },

  /**
   * Check the status of a TikTok publish job
   * @param {string} publishId - The TikTok publish ID
   * @param {string} firebaseUID - User's Firebase UID
   * @returns {Promise<Object>} Status result
   */
  async checkPublishStatus(publishId, firebaseUID) {
    try {
      console.log('[PublishingService] Checking publish status:', {
        publishId,
        firebaseUID: firebaseUID?.substring(0, 10) + '...'
      });

      const response = await api.get(`/publishing/status/${publishId}`, { firebaseUID });
      
      console.log('[PublishingService] Status response:', response);
      return response;
    } catch (error) {
      console.error('[PublishingService] Status check error:', error);
      return {
        success: false,
        error: error.error || 'Failed to check publish status'
      };
    }
  }
};

// --- Media Service ---
export const mediaService = {
  async getLibrary(params = {}) {
    try {
      console.log('MediaService: Fetching library with params:', params);
      const response = await api.get('/media/library', params);
      console.log('MediaService: Library response:', response);
      return response;
    } catch (error) {
      console.error('MediaService: Library fetch error:', error);
      return {
        success: false,
        error: error.error || 'Failed to fetch media library',
        resources: []
      };
    }
  },

  async getSignature(params = {}) {
    try {
      console.log('MediaService: Getting upload signature');
      const response = await api.post('/media/signature', params);
      console.log('MediaService: Signature response:', response);
      return response;
    } catch (error) {
      console.error('MediaService: Signature error:', error);
      return {
        success: false,
        error: error.error || 'Failed to get upload signature'
      };
    }
  },

  async uploadMedia(file, options = {}) {
    try {
      console.log('MediaService: Uploading file:', file.name);
      
      const formData = new FormData();
      formData.append('file', file);
      formData.append('options', JSON.stringify(options));

      const response = await apiClient.post('/media/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }).then(handleResponse).catch(handleError);

      console.log('MediaService: Upload response:', response);
      return response;
    } catch (error) {
      console.error('MediaService: Upload error:', error);
      return {
        success: false,
        error: error.error || 'Failed to upload media'
      };
    }
  },

  async deleteMedia(publicId, resourceType = 'image') {
    try {
      console.log('MediaService: Deleting media:', publicId);
      
      const encodedPublicId = encodeURIComponent(publicId);
      
      const response = await apiClient.delete(`/media/${encodedPublicId}`, {
        params: { resourceType }
      }).then(handleResponse).catch(handleError);
      
      console.log('MediaService: Delete response:', response);
      return response;
    } catch (error) {
      console.error('MediaService: Delete error:', error);
      return {
        success: false,
        error: error.error || 'Failed to delete media'
      };
    }
  },

  async searchMedia(query, filters = {}) {
    try {
      const params = {
        ...filters,
        search: query
      };
      return await this.getLibrary(params);
    } catch (error) {
      console.error('MediaService: Search error:', error);
      return {
        success: false,
        error: error.error || 'Failed to search media',
        resources: []
      };
    }
  }
};

// --- Analytics Service ---
export const analyticsService = {
  getSummary: (params = {}) => api.get('/analytics/summary', params),
  getTopPosts: (params = {}) => api.get('/analytics/top-posts', params),
};

// --- Social Service ---
export const socialService = {
  getConnectedAccounts: (firebaseUID) => {
    console.log('SocialService: Getting connected accounts for UID:', firebaseUID);
    return api.get('/social/connected-accounts', { firebaseUID });
  },
  connectPlatform: (platform, data) => api.post(`/social/${platform}/connect`, data),
  disconnectPlatform: (platform, firebaseUID) => api.post(`/social/${platform}/disconnect`, { firebaseUID }),
  getStatus: (platform, firebaseUID) => api.get(`/social/${platform}/status`, { firebaseUID }),
};

export { apiClient };
